import type { ParsedUrlQuery } from 'querystring';

import { getPageFromParam } from 'src/utils/queryParams';

export interface ProductsQueryActiveStateFromPageURL {
  activeFacetValuesByKey: Record<string, string[]>;
  activeSort: { descending: boolean; key: string } | null;
  page: number;
  searchTerm?: string;
}

interface AdapterOptions {
  includeSearchTerm?: boolean;
}

const FACET_PARAM_PREFIX = 'f_';
const ATTRIBUTES_SHORT_PREFIX = 'a.';
const ATTRIBUTES_FULL_PREFIX = 'attributes.';

const getFirstValue = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value;

const expandFacetKey = (shortKey: string): string =>
  shortKey.startsWith(ATTRIBUTES_SHORT_PREFIX)
    ? `${ATTRIBUTES_FULL_PREFIX}${shortKey.slice(ATTRIBUTES_SHORT_PREFIX.length)}`
    : shortKey;

const queryParamNameToFacetKey = (paramName: string): string | null => {
  if (!paramName.startsWith(FACET_PARAM_PREFIX)) {
    return null;
  }

  return expandFacetKey(paramName.slice(FACET_PARAM_PREFIX.length));
};

const parseSortValue = (
  rawSort: string | undefined,
): { descending: boolean; key: string } | null => {
  if (!rawSort) {
    return null;
  }

  const lastDashIndex = rawSort.lastIndexOf('-');

  if (lastDashIndex === -1) {
    return { descending: false, key: expandFacetKey(rawSort) };
  }

  const direction = rawSort.slice(lastDashIndex + 1);

  if (direction !== 'asc' && direction !== 'desc') {
    return { descending: false, key: expandFacetKey(rawSort) };
  }

  return {
    descending: direction === 'desc',
    key: expandFacetKey(rawSort.slice(0, lastDashIndex)),
  };
};

const collectActiveFacetValues = (query: ParsedUrlQuery): Record<string, string[]> => {
  const activeFacetValuesByKey: Record<string, string[]> = {};

  Object.entries(query).forEach(([paramName, value]) => {
    const facetKey = queryParamNameToFacetKey(paramName);

    if (!facetKey) {
      return;
    }

    const values = Array.isArray(value)
      ? (value as string[])
      : value != null
      ? [String(value)]
      : [];

    if (values.length > 0) {
      activeFacetValuesByKey[facetKey] = Array.from(new Set(values));
    }
  });

  return activeFacetValuesByKey;
};

export const adaptPageURLToProductsQueryActiveState = (
  query: ParsedUrlQuery,
  options: AdapterOptions = {},
): ProductsQueryActiveStateFromPageURL => ({
  activeFacetValuesByKey: collectActiveFacetValues(query),
  activeSort: parseSortValue(getFirstValue(query.sort)),
  page: getPageFromParam(query),
  searchTerm: options.includeSearchTerm ? getFirstValue(query.term) : undefined,
});
